// A shot that sits in no scene ("unplaced") is not in the project data the view is built from, which walks
// episodes → scenes → shots. This file is how such a shot is opened by its id anyway, and then placed:
//   - it is loaded on its own (the shot row, its takes, its generations) and kept beside the placed shots, never
//     among them, so folders still show it as "not in an episode yet" until it is placed;
//   - its generations are kept under a key of its own, where a placed shot's are kept under its episode;
//   - "Place in episode…" puts it in an episode's default scene; the project is reloaded and the shot is at home.
// Pure helpers only, so the rules are tested without a browser.
import type { Episode, Generation, Shot, ShotRow, TakesResponse } from "./types.ts";
import type { ProjectData } from "./useProject.ts";

const PREFIX = "shot:";

/** The key under which the generations of an unplaced shot are kept in `ProjectData.generations`. */
export const looseKey = (shotId: string): string => `${PREFIX}${shotId}`;
export const isLooseKey = (key: string): boolean => key.startsWith(PREFIX);
export const shotIdOfKey = (key: string): string => key.slice(PREFIX.length);

/** The key of a shot's generations: its episode when it sits in one, else its own key. */
export function generationsKey(d: Pick<ProjectData, "shotHome">, shotId: string): string {
  return d.shotHome.get(shotId)?.episodeId ?? looseKey(shotId);
}

/** The generations the project data holds for a shot (placed or not). */
export function generationsOf(
  d: Pick<ProjectData, "shotHome" | "generations">,
  shotId: string,
): Generation[] {
  return d.generations.get(generationsKey(d, shotId)) ?? [];
}

/** Only the shot's own generations: `generationsOf` holds its whole episode, so keep those of its takes. */
export function shotGenerations(
  d: Pick<ProjectData, "shotHome" | "generations" | "takes">,
  shotId: string,
): Generation[] {
  const own = new Set((d.takes.get(shotId)?.items ?? []).map((t) => t.id));
  return generationsOf(d, shotId).filter((g) => g.shot_id === shotId || own.has(g.take_id));
}

/** The shot, whether it sits in a scene or was opened on its own. */
export function findShot(d: ProjectData, shotId: string): Shot | undefined {
  return d.shotById.get(shotId) ?? d.loose?.get(shotId);
}

/** Does the shot sit in no scene? (Only meaningful for a shot the data holds.) */
export const isUnplaced = (d: ProjectData, shotId: string): boolean =>
  !d.shotHome.has(shotId) && !!d.loose?.has(shotId);

/** The project data with one unplaced shot added: its row, takes and generations. */
export function withLooseShot(
  d: ProjectData,
  shot: Shot,
  takes: TakesResponse,
  generations: Generation[],
): ProjectData {
  return {
    ...d,
    loose: new Map(d.loose).set(shot.id, shot),
    takes: new Map(d.takes).set(shot.id, takes),
    generations: new Map(d.generations).set(looseKey(shot.id), generations),
  };
}

/** What the page shows while an unplaced shot is being opened. */
export type LooseState =
  | { s: "ready" }
  | { s: "loading" }
  /** gone, archived, or in another project */
  | { s: "missing" }
  | { s: "error"; message: string };

/** The state for a failed load: a 404 means gone; anything else can be tried again. */
export function looseStateOfError(e: unknown): LooseState {
  const status = (e as { status?: number } | null)?.status;
  if (status === 404) return { s: "missing" };
  const message = e instanceof Error ? e.message : String(e);
  return { s: "error", message };
}

/** The row must be a live shot of this project, or it is "not found" (never open another project's shot). */
export function checkShotRow(row: ShotRow, projectId: string): LooseState {
  return row.project_id === projectId && !row.archived_at ? { s: "ready" } : { s: "missing" };
}

/** Episodes a shot can be placed in, in order. */
export const placeableEpisodes = (d: Pick<ProjectData, "episodes">): Episode[] =>
  [...d.episodes].sort((a, b) => a.number - b.number);

/** What the shot view says about where the shot sits. */
export type Placement =
  | { s: "placed" }
  /** in no episode: a director can place it, others are told */
  | { s: "unplaced"; canPlace: boolean; noEpisodes: boolean }
  /** placed a moment ago in this view (the project reloads behind it) */
  | { s: "just-placed"; episodeId: string };

export function placementOf(
  d: ProjectData,
  shotId: string,
  canPlace: boolean,
  justPlaced: string | null,
): Placement {
  if (justPlaced) return { s: "just-placed", episodeId: justPlaced };
  if (d.shotHome.has(shotId)) return { s: "placed" };
  return { s: "unplaced", canPlace, noEpisodes: d.episodes.length === 0 };
}
